import React, { useState } from 'react';

import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { APIEndpoint } from 'common/enum';
import CustomModal from 'components/CustomModal';
import useNavigateWithUUID from 'hooks/useNavigateWithUUID';
import { popupNotification } from 'notifications/ToastNotification';
import { useDrag } from 'react-dnd';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { v4 as uuidv4 } from 'uuid';

import { ItemTypes } from './PDFSignatureViewer';

interface SigneesSidebarProps {
  onSidebarFieldDragStart: () => void;
  onFinishAndSend: () => void;
  onSignClick: () => void;
  userSignature: { type: 'draw' | 'type'; value: string } | null;
  onEditSignature: () => void;
  onDownload: () => void;
  signees: Array<{
    name: string;
    email: string;
    color: string;
    signeeType?: string;
    level?: number;
    fields: Array<{
      type: string;
      label: string;
    }>;
  }>;
  pdfFile: string | null;
  fields: any[];
  contractDetails?: {
    levels: number;
    yearToRetain: number;
    creatorRole: string;
    contractName: string;
    formData?: any[];
    levelData?: Array<{
      level: number;
      signees: Array<{
        name: string;
        email: string;
        signeeType: string;
        color: string;
        level: number;
        fields: Array<{
          type: string;
          label: string;
        }>;
      }>;
    }>;
  };
  pdfFilePath?: string;
  generatePdfWithFields?: () => Promise<Uint8Array>;
  uuid?: string;
  folderId?: string | null;
  signaturePositions?: Array<{
    id?: string;
    page: number;
    x: number;
    y: number;
    signed?: boolean;
    signatureUrl?: string;
    signeeName?: string;
    signeeEmail?: string;
    signeeType?: string;
    source?: 'new' | 'loaded';
    signeeId?: string;
    fieldType?: 'signature' | 'initials';
  }>;
  generatePayloadWithSigneeUuids: () => {
    signees: Array<{
      signeeId: string;
      signeeName: string;
      signeeEmail: string;
      signeeType: string;
      fields: any[];
    }>;
    totalFields: number;
    contractDetails?: any;
  };
}

// Draggable signature field component from reference implementation
const DraggableSignatureField: React.FC<{
  signee: { name: string; email: string; color: string; signeeType?: string };
  field: { type: string; label: string };
}> = ({ signee, field }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.SIGN_BUTTON,
    item: () => {
      const dragItem = {
        id: `${signee.email}-${field.type}-${Date.now()}`,
        signeeEmail: signee.email,
        signeeName: signee.name,
        signeeType: signee.signeeType,
        fieldType: field.type,
        label: field.label,
      };

      return dragItem;
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <Box
      ref={drag as any}
      sx={{
        padding: '8px',
        margin: '5px 0',
        backgroundColor: signee.color,
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        width: '100%',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed gray',
        borderRadius: '4px',
        color: 'black',
        fontWeight: 'bold',
      }}
    >
      {field.label}
    </Box>
  );
};

const SigneesSidebar: React.FC<SigneesSidebarProps> = ({
  onSidebarFieldDragStart,
  onFinishAndSend,
  onSignClick,
  userSignature,
  onEditSignature,
  onDownload,
  signees,
  pdfFile,
  fields,
  contractDetails,
  pdfFilePath,
  generatePdfWithFields,
  uuid,
  folderId,
  signaturePositions = [],
  generatePayloadWithSigneeUuids,
}) => {
  // Get user data from auth state
  const { username, firstName, lastName, accessToken } = useSelector(
    (state: RootState) => state.auth
  );

  // Add state for success dialog and loading
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // Navigation hook
  const { navigateWithUuid } = useNavigateWithUUID();
  // Custom upload function for SigneesSidebar
  const uploadContractFile = async (file: File) => {
    try {
      const fileName = `${uuid}/${folderId ? folderId + '/' : ''}Home/Contracts/${file.name}`;

      const { data } = await axios.post(
        APIEndpoint.uploadFile,
        {
          fileName: fileName,
          uploadedBy: `${firstName || ''} ${lastName || ''}`.trim(),
          docType: 'contract',
          yearToRetain: contractDetails?.yearToRetain || 0,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + accessToken,
          },
        }
      );

      const uploadUrl = data.upload_url.url;
      const formData = new FormData();

      if (data.upload_url.fields) {
        Object.entries(data.upload_url.fields).forEach(([k, v]: any) => {
          formData.append(k, v);
        });
      }

      formData.append('file', file);

      await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // popupNotification(
      //   'Contract uploaded successfully to Contracts folder',
      //   true
      // );
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error uploading contract:', error);
      popupNotification('Error uploading contract. Please try again.', false);
    } finally {
      setIsUploading(false);
    }
  };

  // Use provided signees directly
  const displaySignees = signees;

  // Validation function to check if all signees have at least one signature or initials field
  const validateAllSigneesHaveFields = () => {
    const signeesWithoutFields: string[] = [];

    signees.forEach((signee) => {
      // Check if this signee has any signature or initials fields placed on the PDF
      const hasFields = signaturePositions.some(
        (position) =>
          position.signeeEmail === signee.email &&
          (position.fieldType === 'signature' ||
            position.fieldType === 'initials')
      );

      if (!hasFields) {
        signeesWithoutFields.push(signee.name);
      }
    });

    return {
      isValid: signeesWithoutFields.length === 0,
      missingSignees: signeesWithoutFields,
    };
  };

  const handleFinishAndSend = async () => {
    try {
      // Validate that all signees have at least one signature or initials field
      const validation = validateAllSigneesHaveFields();

      if (!validation.isValid) {
        const message = `\n\n${validation.missingSignees.join('\n')}\n\nPlease add at least one signature or initials field for each signee before proceeding.`;
        setValidationMessage(message);
        setShowValidationDialog(true);
        return;
      }

      // Generate payload with signee UUIDs
      const payload = generatePayloadWithSigneeUuids();

      // Create contract data with signee UUIDs
      const contractData = {
        contractLocation: '', // Will be updated later with actual file path
        signaturesRequired: contractDetails?.levels || 0,
        httpMethod: 'POST',
        actionName: 'CreateContract',
        contractDetails: {
          levels:
            contractDetails?.formData?.map((levelData, index) => ({
              levelId: index + 1,
              signers: levelData.fields.map((signer: any) => {
                // Find the signee UUID for this signer
                const signeeData = payload.signees.find(
                  (s) => s.signeeEmail === signer.email
                );
                // Ensure we always use the same UUID for the same email
                const signeeId =
                  signeeData?.signeeId ||
                  (() => {
                    console.warn(
                      `No signee UUID found for email: ${signer.email}, this should not happen`
                    );
                    return uuidv4(); // Fallback only if something went wrong
                  })();
                return {
                  email: signer.email,
                  signed: false,
                  timestamp: null,
                  signerFullName: signer.name,
                  uuid: signeeId, // Use consistent signee UUID
                };
              }),
              isCompleted: false,
              isLastLevel: index === (contractDetails?.levels || 0) - 1,
              signedSoFar: 0,
              requiredSignatures: levelData.fields.length,
            })) || [],
          originatorRole: contractDetails?.creatorRole || 'Realtor',
          originatorEmail: username,
          documentTemplate: 'Rental Agreement',
          contractName: contractDetails?.contractName || 'Document',
          originatorUserName: `${firstName || ''} ${lastName || ''}`.trim(),
          yearToRetain: contractDetails?.yearToRetain || 0,
        },
        // Add signee UUIDs data
        signeeData: payload.signees.map((signee) => ({
          uuid: signee.signeeId,
          signeeName: signee.signeeName,
          signeeEmail: signee.signeeEmail,
          signeeType: signee.signeeType,
          fieldCount: signee.fields.length,
          fields: signee.fields.map((field) => ({
            fieldId: field.id,
            page: field.page,
            x: field.x,
            y: field.y,
            signed: field.signed || false,
            signatureUrl: field.signatureUrl || '',
          })),
        })),
      };

      // Generate the final PDF with signatures if the function is available
      if (generatePdfWithFields) {
        const pdfBytes = await generatePdfWithFields();

        // Convert PDF bytes to File object
        const pdfBlob = new Blob([pdfBytes as BlobPart], {
          type: 'application/pdf',
        });
        const contractFileName = `contract_${Date.now()}.pdf`;
        const pdfFile = new File([pdfBlob], contractFileName, {
          type: 'application/pdf',
        });

        // Calculate the contract location first
        const contractLocation = `${uuid}/${folderId ? folderId + '/' : ''}Home/Contracts/${contractFileName}`;

        // Update contract data with the correct location
        if (contractData) {
          contractData.contractLocation = contractLocation;
        }

        // function to save the contract json payload
        const saveContractPayload = async (contractData: any) => {
          try {
            setIsUploading(true);
            const response = await axios.post(
              APIEndpoint.secDocsMain,
              contractData,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (response.status === 200) {
              // popupNotification('Contract saved successfully', true);
            } else {
              // popupNotification('Error saving contract payload', false);
            }
          } catch (error) {
            console.error('Error saving contract payload:', error);
            popupNotification('Error saving contract payload', false);
          }
        };

        // Save the contract JSON payload with correct location
        if (contractData) {
          await saveContractPayload(contractData);
        }

        // Upload the contract using custom function
        if (uuid) {
          await uploadContractFile(pdfFile);
        }
      }

      // Call the original onFinishAndSend function
      onFinishAndSend();
    } catch (error) {
      console.error('Error in handleFinishAndSend:', error);
      popupNotification('Error uploading contract. Please try again.', false);
    }
  };

  const handleDownload = () => {
    // Validate that all signees have at least one signature or initials field
    const validation = validateAllSigneesHaveFields();

    if (!validation.isValid) {
      const message = `\n\n${validation.missingSignees.join('\n')}\n\nPlease add at least one signature or initials field for each signee before proceeding.`;
      setValidationMessage(message);
      setShowValidationDialog(true);
      return;
    }

    // If validation passes, proceed with download
    onDownload();
  };

  return (
    <>
      {/* Full-page overlay loader */}
      {isUploading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3, color: 'white' }} />
          <Typography variant="h6" color="white" textAlign="center" mb={2}>
            Uploading Contract...
          </Typography>
          <Typography
            variant="body1"
            color="white"
            textAlign="center"
            sx={{ opacity: 0.8 }}
          >
            Please wait while we process and upload your contract
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          width: 300,
          backgroundColor: '#f5f5f5',
          boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
          borderLeft: '1px solid #ddd',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative',
          height: 'calc(100vh - 80px)', // Fixed height to enable scrolling
          overflow: 'hidden', // Prevent outer container from scrolling
          mt: 2,
          borderTopRightRadius: 18,
          borderTopLeftRadius: 18,
        }}
      >
        {/* Overlay Loader for sidebar only */}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              borderRadius: 1,
            }}
          >
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Uploading contract...
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1 }}
            >
              Please wait while we process your contract
            </Typography>
          </Box>
        )}
        <Typography variant="h6" gutterBottom>
          Signees for contract:
          <Typography
            variant="body1"
            ml={2}
            gutterBottom
            fontWeight="bold"
            component="span"
            textTransform="uppercase"
          >
            {contractDetails?.contractName}
          </Typography>
        </Typography>

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 1,

            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}
        >
          {/* Signees and their fields */}
          {displaySignees.length > 0 ? (
            displaySignees.map((signee, index) => (
              <Card key={index} sx={{ mb: 2, width: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <span style={{ fontWeight: 'bold' }}>Name:</span>{' '}
                    {signee.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <span style={{ fontWeight: 'bold' }}>Email:</span>{' '}
                    {signee.email}
                  </Typography>
                  {signee.signeeType && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      <span style={{ fontWeight: 'bold' }}>Type:</span>{' '}
                      {signee.signeeType}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" gutterBottom>
                    Drag signature and initials fields to PDF:
                  </Typography>
                  {signee.fields.filter(
                    (field) =>
                      field.type === 'signature' || field.type === 'initials'
                  ).length > 0 ? (
                    signee.fields
                      .filter(
                        (field) =>
                          field.type === 'signature' ||
                          field.type === 'initials'
                      )
                      .map((field, fieldIndex) => (
                        <DraggableSignatureField
                          key={fieldIndex}
                          signee={signee}
                          field={field}
                        />
                      ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                      sx={{ py: 2 }}
                    >
                      No signature or initials fields available for this signee.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Card sx={{ mb: 2, width: '100%' }}>
              <CardContent>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  No signees available. Please add signees to continue.
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* User signature section */}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleFinishAndSend}
            fullWidth
            sx={{
              background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.9rem',
              padding: '4px 10px',
              borderRadius: '8px',
              boxShadow: '0 3px 8px rgba(46, 204, 113, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #229954 0%, #27ae60 100%)',
                boxShadow: '0 4px 12px rgba(46, 204, 113, 0.4)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
                color: '#adb5bd',
                boxShadow: 'none',
              },
            }}
            startIcon={<SendIcon />}
          >
            Finish & Send
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownload}
            fullWidth
            sx={{
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.85rem',
              padding: '4px 16px',
              borderRadius: '6px',
              borderWidth: '2px',
              '&:hover': {
                backgroundColor: 'rgba(52, 152, 219, 0.04)',
                borderWidth: '2px',
              },
              '&:disabled': {
                borderColor: '#e9ecef',
                color: '#adb5bd',
              },
            }}
            startIcon={<CloudDownloadIcon />}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      {/* Success Dialog */}
      <CustomModal
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        type="success"
        title="✅ Contract Uploaded Successfully!"
        primaryButtonLabel="Continue"
        primaryButtonAction={() => {
          setShowSuccessDialog(false);
          navigateWithUuid('contract-management');
        }}
        autoClose={false}
      >
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
          Your contract has been successfully uploaded to the Contracts folder.
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center' }}
        >
          The contract is now ready for signing and can be accessed by all
          parties involved.
        </Typography>
      </CustomModal>

      {/* Validation Dialog */}
      <CustomModal
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        type="warning"
        title="⚠️ Missing Signature Fields"
        primaryButtonLabel="OK"
        primaryButtonAction={() => setShowValidationDialog(false)}
        autoClose={false}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          The following signees do not have any signature or initials fields
          placed on the PDF:
        </Typography>
        <Box sx={{ mb: 2 }}>
          {validationMessage.split('\n').map((line, index) => {
            // Check if this line contains a signee name (not empty and not the header/footer text)
            const isSigneeName =
              line.trim() &&
              !line.includes('The following signees') &&
              !line.includes('Please add at least one') &&
              !line.includes('before proceeding');

            if (isSigneeName) {
              return (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: '#ffebee', // Light red background
                    color: '#d32f2f', // Red text
                    padding: '8px 12px',
                    margin: '4px 0',
                    borderRadius: '4px',
                    border: '1px solid #ffcdd2',
                    fontWeight: 'bold',
                  }}
                >
                  {line}
                </Box>
              );
            } else if (line.trim()) {
              return (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  {line}
                </Typography>
              );
            }
            return null;
          })}
        </Box>
      </CustomModal>
    </>
  );
};

export default SigneesSidebar;
